import { useState } from "react";
import { Link } from "react-router-dom";
import {
  type SystemAlert,
  type SystemAlertsResult,
  fetchSystemAlerts,
} from "../../api/admin-system";
import { t } from "../../i18n";
import * as shared from "../../styles/shared";

export function SystemAlertsPanel(): React.JSX.Element {
  const [result, setResult] = useState<SystemAlertsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function refreshAlerts(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      setResult(await fetchSystemAlerts());
    } catch (err) {
      setError(err instanceof Error ? err.message : t("health.alerts_failed"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <section style={shared.section}>
      <div style={s.sectionHeader}>
        <div>
          <h2 style={shared.sectionTitle}>{t("health.active_alerts")}</h2>
          {result ? (
            <p style={s.meta}>
              {t("health.alerts_checked_at")}{" "}
              {new Date(result.checked_at).toLocaleTimeString()}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void refreshAlerts()}
          disabled={loading}
          style={shared.refreshBtn}
        >
          {t("common.refresh")}
        </button>
      </div>
      {error ? <p style={shared.errorBanner}>{error}</p> : null}
      {loading ? <p style={shared.loadingText}>{t("common.loading")}</p> : null}
      {!loading && !result ? (
        <p style={s.meta}>{t("health.alerts_not_checked")}</p>
      ) : null}
      {result ? <AlertList alerts={result.alerts} /> : null}
    </section>
  );
}

function AlertList({
  alerts,
}: {
  alerts: ReadonlyArray<SystemAlert>;
}): React.JSX.Element {
  if (alerts.length === 0) {
    return <p style={s.healthy}>{t("health.no_active_alerts")}</p>;
  }
  return (
    <table style={s.table}>
      <thead>
        <tr>
          <th style={s.th}>{t("health.alert_level")}</th>
          <th style={s.th}>{t("health.alert_title")}</th>
          <th style={s.th}>{t("health.alert_detail")}</th>
          <th style={s.th}>{t("health.alert_runbook")}</th>
        </tr>
      </thead>
      <tbody>
        {alerts.map((alert) => (
          <AlertRow key={alert.id} alert={alert} />
        ))}
      </tbody>
    </table>
  );
}

function AlertRow({ alert }: { alert: SystemAlert }): React.JSX.Element {
  return (
    <tr>
      <td style={s.td}>
        <span style={{ ...shared.badge, background: levelColor(alert.level) }}>
          {alert.level}
        </span>
      </td>
      <td style={s.td}>
        <Link to={adminDrilldownPath(alert.drilldown_url)} style={s.link}>
          {alert.title}
        </Link>
      </td>
      <td style={s.td}>{alert.detail}</td>
      <td style={s.td}>
        {alert.runbook ? (
          <span style={s.runbook}>
            {alert.runbook.owner} - {alert.runbook.first_action}
          </span>
        ) : (
          t("api_coverage.none")
        )}
      </td>
    </tr>
  );
}

function adminDrilldownPath(path: string): string {
  if (path.startsWith("/admin/summary-jobs")) {
    return path.replace("/admin/summary-jobs", "/content/summary-jobs");
  }
  if (path.startsWith("/admin/storage")) {
    return path.replace("/admin/storage", "/content/storage");
  }
  if (path.startsWith("/admin/system/queues")) return "/system/health";
  if (path.startsWith("/admin/billing")) return "/revenue/billing";
  return path.replace(/^\/admin/u, "") || "/";
}

function levelColor(level: SystemAlert["level"]): string {
  if (level === "P1") return "#dc2626";
  if (level === "P2") return "#f59e0b";
  return "#64748b";
}

const s: Record<string, React.CSSProperties> = {
  sectionHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },
  meta: {
    margin: 0,
    color: "var(--color-text-secondary)",
    fontSize: 13,
  },
  healthy: {
    padding: "10px 12px",
    border: "1px solid #bbf7d0",
    borderRadius: 6,
    background: "#dcfce7",
    color: "var(--color-text)",
    fontSize: 13,
    fontWeight: 600,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    padding: "8px 12px",
    borderBottom: "2px solid var(--color-border)",
    color: "var(--color-text-secondary)",
    fontSize: 12,
    fontWeight: 600,
    textAlign: "left",
    textTransform: "uppercase",
  },
  td: {
    padding: "9px 12px",
    borderBottom: "1px solid var(--color-border)",
    verticalAlign: "top",
  },
  link: {
    color: "var(--color-primary)",
    fontWeight: 700,
    textDecoration: "none",
  },
  runbook: {
    color: "var(--color-text-secondary)",
  },
};
